import { create } from 'zustand';
import type { Connection, SaveConnectionOptions } from '../../../types/electron';
import type { ActiveConnection } from '../models/connectionTypes';
import { getConnections, saveConnection, deleteConnection as deleteConnectionService, getDecryptedPassword, touchConnectionLastConnected } from '../services/connectionService';
import {
  openConnection,
  closeConnection,
  setConnectIntent,
} from '../services/sqlConnectionService';
import { classifyDbError } from '../../../core/analytics';
import { connectDurationBucket } from '../../../core/analytics/telemetryBuckets';
import type { DbErrorClassification } from '../../../core/analytics/dbErrorClassifier';
import { addBreadcrumb } from '../../crash-dialog/services/breadcrumbService';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { invalidateRoutines } from '../../workspace/common/utils/routineParameterResolver';
import { invalidateColumns } from '../../workspace/common/utils/insertHintColumnResolver';
import { forgetMysqlSqlMode } from '../../workspace/common/utils/mysqlSqlMode';
import { invalidateConnection } from '../../intellisense/schemaBus/schemaBus';
import { clearSchemaRefreshesForConnection } from '../../intellisense/schemaRefresh/schemaRefreshStore';
import { connectionRequiresPoolReset } from '../utils/connectionPoolReset';
import { changedDialFields } from '../utils/dialProjection';
import {
  sshTunnelIdentityKey,
  stripTransientSshSecrets,
  transientSshSecretsFromOptions,
  withTransientSshSecrets,
  type TransientSshSecrets,
} from '../utils/sshTransientSecrets';
import {
  connectionEditChangesDatabaseListing,
  getLimitedDatabase,
  isSameDatabaseName,
} from '../utils/connectionDatabaseLimit';
import { getSharedQueryClient } from '../../../core/query/sharedQueryClient';
import { databaseObjectsQueryKey } from '../../database-objects/services/databaseObjectsQueryKeys';
import { connectionNeedsPasswordPrompt } from '../utils/connectPasswordPredicate';
import { isConnectCancelledError, isConnectRefusedByDelete } from '../utils/connectCancellation';
import { requestConnectPassword } from './connectPasswordPromptStore';
import {
  shouldConsiderConnectForBridge,
  type ConnectInitiator,
} from '../../workspace/first-connect-bridge/firstConnectBridgeFlags';
import { redactSecretText } from '../../../types/safeConnectionDisplay';

type ConnectionOperation = 'connecting' | 'disconnecting';

/**
 * Track analytics event from store (non-React context).
 * Fire-and-forget pattern - analytics errors are silently ignored.
 *
 * The engine is carried through AS-IS (no allowlist / coercion) so Oracle
 * and every other engine are labeled correctly. Absent engine is simply
 * omitted rather than mislabeled.
 */
function trackConnectionEvent(
  eventName: string,
  engine?: string,
  /**
   * Extra properties for this one event. Same rule as everywhere else in
   * analytics: bucketed / categorical values only — never a name, a host, an
   * id, or a duration in milliseconds.
   */
  extra?: Record<string, string | number | boolean>
): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;

  const data: Record<string, string | number | boolean> = { ...extra };
  if (engine) {
    data.engine = engine;
  }

  window.electronAPI.analytics.track(eventName, data).catch(() => {
    // Silently ignore analytics errors
  });
}

/**
 * Repeated identical connect failures fire in bursts: session restore probes
 * every connection of the session, several surfaces redial the same dead
 * server, and each settled attempt would emit its own `connection:error`
 * (one storm session produced 46 in 4.5 minutes). The funnel metrics count
 * distinct sessions, so one event per distinct failure per minute carries the
 * same signal — identical (connection, engine, category, code) failures inside
 * the window are dropped.
 */
const CONNECTION_ERROR_TRACK_COOLDOWN_MS = 60_000;
const connectionErrorLastTrackedAt = new Map<string, number>();

/** Test-only: clear the `connection:error` dedupe window between specs. */
export function resetConnectionErrorTrackCooldown(): void {
  connectionErrorLastTrackedAt.clear();
}

/**
 * Track connection error event with sanitized error category.
 * NEVER includes sensitive data (server names, file paths, SQL, stack traces).
 *
 * The engine is carried through AS-IS; `'unknown'` is used only when the
 * engine is genuinely absent (never coerced to a real engine name).
 */
function trackConnectionError(
  connectionId: string,
  engine?: string,
  classification?: DbErrorClassification
): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;

  const data: Record<string, string | number | boolean> = {
    engine: engine ?? 'unknown',
    category: classification?.category ?? 'unknown',
    // Low-cardinality driver error code only (ELOGIN, 28P01, ORA-01017, …) —
    // sanitized by classifyDbError, never raw text.
    ...(classification?.code ? { code: classification.code } : {}),
  };

  const cooldownKey = `${connectionId}|${data.engine}|${data.category}|${data.code ?? ''}`;
  const now = Date.now();
  const lastTrackedAt = connectionErrorLastTrackedAt.get(cooldownKey);
  if (lastTrackedAt !== undefined && now - lastTrackedAt < CONNECTION_ERROR_TRACK_COOLDOWN_MS) {
    return;
  }
  connectionErrorLastTrackedAt.set(cooldownKey, now);

  window.electronAPI.analytics.track('connection:error', data).catch(() => {
    // Silently ignore analytics errors
  });
}
/**
 * A connect that is still dialing is owned by nobody — not by
 * `activeConnections` (the entry is only added on success) and not by the
 * main process (`ownsConnection` is pool existence, so a disconnect issued
 * mid-dial is a silent no-op and the pool lands anyway).
 *
 * So "the user cancelled" cannot be acted on when it happens; it can only be
 * *remembered* and acted on when the connect settles. That is what this
 * record is: the standing intent behind an in-flight connect.
 *
 * `epoch` is the intent this attempt is currently authorized by. It is stamped
 * when the attempt starts, RE-stamped whenever a caller joins (a join is a
 * fresh expression of intent — which is what makes cancel-then-retry land
 * normally instead of racing a half-torn-down dial), and compared against the
 * connection's current epoch when the attempt settles. Different means someone
 * withdrew afterwards: last expression of intent wins.
 *
 * Refcounting the waiters instead does NOT work, and failed in exactly the way
 * that matters: several call sites join a connect and simply never cancel — a
 * resume joins its own validation probe — so the count never reaches zero and
 * the cancel is silently ignored. Fail-closed is the right default here: a
 * connection released out from under a concurrent joiner reports as
 * not-connected and is one click away, whereas a cancel that does nothing is
 * the bug this exists to kill.
 */
type ConnectAttempt = {
  promise: Promise<boolean>;
  epoch: number;
  /**
   * Names this attempt in the main process, so a withdrawal can close exactly
   * the pool this attempt opened and nothing else. See
   * `electron/connection-managers/connectAttemptRegistry.ts`.
   */
  token: string;
};

let mintedAttempts = 0;
const mintAttemptToken = (connectionId: string): string => {
  mintedAttempts += 1;
  return `${connectionId}#${mintedAttempts}`;
};

const inFlightConnectRequests = new Map<string, ConnectAttempt>();
const inFlightDisconnectRequests = new Map<string, Promise<boolean>>();

/**
 * Ids whose `delete-connection` IPC is out right now (round 5).
 *
 * A deleted id must never end connected, and the store had two ways to undo
 * main's refusal of the connects around a delete: a connect that STARTED during
 * the delete IPC (a lazy connect, a session restore, an agent's
 * `connections_connect`) dialled, and a join of the withdrawn dial re-stamped
 * its epoch so that main's refusal was retried "with force" once the delete had
 * settled — the retry dialled the deleted connection back to life and the
 * post-connect save re-created its record. While an id is here,
 * `connectToDatabase` refuses it before the short-circuit and before the join.
 */
const deletingConnectionIds = new Set<string>();

/**
 * Monotonic per-connection counter, bumped every time someone withdraws a
 * connect intent.
 *
 * It outlives the attempt record, and serves two readers: the attempt itself
 * (release the pool on arrival rather than adopt it) and any continuation that
 * writes *session* bookkeeping (lazy connect, session restore), which captures
 * it at request time and re-checks before writing — so a withdrawn intent can
 * never re-add what the user just removed.
 */
const connectIntentEpochs = new Map<string, number>();

const currentIntentEpoch = (connectionId: string): number =>
  connectIntentEpochs.get(connectionId) ?? 0;

/** True when someone withdrew their intent after this attempt was authorized. */
const isAttemptWithdrawn = (connectionId: string, attempt: ConnectAttempt): boolean =>
  attempt.epoch !== currentIntentEpoch(connectionId);

/**
 * Release a pool that landed after its intent was withdrawn.
 *
 * Token-scoped rather than a plain `closeConnection(connectionId)`: main keys
 * pools by bare connection id, so closing by id would just as happily tear
 * down a pool a *newer* attempt opened for the same connection. The token
 * makes the teardown apply to this attempt's pool or to nothing.
 *
 * It also deliberately avoids the store's `disconnectFromDatabase`: nothing
 * was ever adopted into `activeConnections`, and routing through the action
 * would fire a `connection:disconnect` analytics event for a disconnect the
 * user never performed (the matching `connection:connect` is skipped here).
 */
async function releaseWithdrawnConnect(
  connectionId: string,
  attempt: ConnectAttempt
): Promise<void> {
  try {
    await setConnectIntent(connectionId, attempt.token, false);
  } catch {
    // Best effort — the pool may never have registered in main at all.
  }
}

/**
 * The SSH secrets typed in this session, per connection id — a bastion
 * password or key passphrase the user entered in the dialog and did not (or
 * did) keep in the keychain (round 5, F5).
 *
 * They arrive as save OPTIONS (`sshPassword` / `sshPassphrase`), never on the
 * record: the store keeps whatever connection object it is handed, and a
 * record field rode every later background re-save into the reset predicate as
 * "a credential the user just typed". What the dial still needs — the tunnel
 * manager reads the typed value off `sshTunnel` when no stored blob exists —
 * is attached to the DIAL config only, from here, and forgotten with the
 * record. Never persisted, never in `connections[]`.
 *
 * Keyed by connection id AND SSH TARGET (A5). A typed secret belongs to the
 * bastion it was typed for; keyed by id alone, editing a connection's SSH host,
 * user, key path or port and dialling again handed the OLD bastion's password
 * to the NEW one — the rebinding the stored secrets' identity tag prevents,
 * missing for typed ones. Storing under the target's key makes the lookup its
 * own gate: the dial finds a secret only when the target still matches, and the
 * entry for a target the user has moved off is dropped at the same moment.
 */
const sessionSshSecretsByTarget = new Map<string, TransientSshSecrets>();

const sessionSshSecretsKey = (connectionId: string, identity: string): string =>
  `${connectionId}\u0000${identity}`;

/**
 * Remember what this save typed, under the SAVED record's SSH target — and drop
 * whatever was remembered for any OTHER target of the same connection, because
 * the record no longer names it.
 */
function rememberSessionSshSecrets(connection: Connection, options: SaveConnectionOptions | undefined): void {
  const identity = sshTunnelIdentityKey(connection);
  if (identity === undefined) {
    forgetSessionSshSecrets(connection.id);
    return;
  }
  const key = sessionSshSecretsKey(connection.id, identity);
  for (const existing of [...sessionSshSecretsByTarget.keys()]) {
    if (existing !== key && existing.startsWith(`${connection.id}\u0000`)) {
      sessionSshSecretsByTarget.delete(existing);
    }
  }
  const typed = transientSshSecretsFromOptions(options);
  if (!typed) return;
  sessionSshSecretsByTarget.set(key, { ...sessionSshSecretsByTarget.get(key), ...typed });
}

/** What this dial may use: only a secret typed for the target it is dialling. */
function sessionSshSecretsFor(connection: Connection): TransientSshSecrets | undefined {
  const identity = sshTunnelIdentityKey(connection);
  if (identity === undefined) return undefined;
  return sessionSshSecretsByTarget.get(sessionSshSecretsKey(connection.id, identity));
}

/** Forget every target's secrets for a connection — it was deleted. */
function forgetSessionSshSecrets(connectionId: string): void {
  for (const key of [...sessionSshSecretsByTarget.keys()]) {
    if (key.startsWith(`${connectionId}\u0000`)) sessionSshSecretsByTarget.delete(key);
  }
}

interface ConnectionState {
  // State
  connections: Connection[];
  activeConnections: ActiveConnection[];
  connectionOperationById: Record<string, ConnectionOperation | undefined>;
  connectionErrorById: Record<string, string | undefined>;
  /**
   * Structured driver error code for the failure in `connectionErrorById`
   * (e.g. ELOGIN, 28P01) — kept in lockstep with it so error surfaces can
   * classify code-only/localized failures. Never raw text.
   */
  connectionErrorCodeById: Record<string, string | undefined>;
  loading: boolean;

  // Actions
  loadConnections: () => Promise<void>;
  addConnection: (connection: Connection, options?: SaveConnectionOptions) => Promise<boolean>;
  /**
   * `options` describes THIS save only (`SaveConnectionOptions`): the dialog
   * passes `passwordEdited` when the user retyped the password, and the SSH
   * secrets it typed as `sshPassword` / `sshPassphrase`; both the main-side
   * teardown and the reset check below read them from the argument — never
   * from the record, which is stored and re-saved in the background.
   */
  updateConnection: (connection: Connection, options?: SaveConnectionOptions) => Promise<boolean>;
  deleteConnection: (connectionId: string) => Promise<boolean>;
  /**
   * `opts.force` skips the already-connected short-circuit and rebuilds
   * pools/auth state. `opts.initiator` distinguishes user-initiated connects
   * from session-restore/auto-reconnect so the first-connect bridge only fires
   * for the former (defaults to user when omitted).
   */
  connectToDatabase: (
    connection: Connection,
    opts?: { force?: boolean; initiator?: ConnectInitiator }
  ) => Promise<boolean>;
  disconnectFromDatabase: (connectionId: string) => Promise<boolean>;
  /**
   * Withdraw this caller's interest in an in-flight connect (Cancel, session
   * switch, "forget this lazy connect"). The dial itself cannot be aborted —
   * see `ConnectAttempt` — so this records the withdrawal: when the connect
   * settles, an attempt nobody is waiting on any more is closed instead of
   * adopted, and its bookkeeping is never written.
   *
   * Safe to call unconditionally; a no-op when nothing is in flight.
   */
  abandonConnect: (connectionId: string) => void;
  /**
   * Current connect-intent epoch for a connection. Capture before awaiting a
   * connect, re-check before writing session state — see `connectIntentEpochs`.
   */
  getConnectIntentEpoch: (connectionId: string) => number;
  /** Mark a connection as disconnected without removing it from activeConnections */
  markConnectionDisconnected: (connectionId: string) => void;
  /** Update the live SSH tunnel state for a connection (driven by ssh:tunnel-state IPC events). */
  setTunnelStateForConnection: (connectionId: string, tunnelState: 'connecting' | 'up' | 'down' | 'error') => void;
}
export const useConnectionStore = create<ConnectionState>((set, get) => ({
  // Initial state
  connections: [],
  activeConnections: [],
  connectionOperationById: {},
  connectionErrorById: {},
  connectionErrorCodeById: {},
  loading: true,
  
  // Actions
  loadConnections: async () => {
    try {
      set({ loading: true });
      const savedConnections = await getConnections();
      set({ connections: savedConnections, loading: false });
      return;
    } catch (error) {
      console.error('Failed to load connections:', error);
      set({ loading: false });
    }
  },
  
  addConnection: async (connection: Connection, options?: SaveConnectionOptions) => {
    try {
      // The record never carries the typed SSH secrets; this session does.
      const record = stripTransientSshSecrets(connection);
      const success = await saveConnection(record, options);

      if (success) {
        rememberSessionSshSecrets(record, options);
        set((state) => ({
          connections: [record, ...state.connections.filter((c) => c.id !== connection.id)],
        }));
        trackConnectionEvent('connection:add', connection.engine);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving connection:', error);
      return false;
    }
  },

  updateConnection: async (connection: Connection, options?: SaveConnectionOptions) => {
    try {
      const prior = get().connections.find(c => c.id === connection.id);
      // The record never carries the typed SSH secrets; this session does.
      const record = stripTransientSshSecrets(connection);
      const success = await saveConnection(record, options);

      if (success) {
        rememberSessionSshSecrets(record, options);
        set(state => ({
          connections: state.connections.map(c =>
            c.id === connection.id ? record : c
          ),
          // Keep the live entry's snapshot in step with the edit. Surfaces that
          // list ACTIVE connections (Data Compare setup, …) read
          // `active.connection.alias` etc., and that object was captured at
          // connect time — so a renamed, still-connected connection kept its
          // old alias there until a reconnect. Both copies are the stripped
          // record (the dial config with typed SSH secrets is never stored),
          // so replacing the snapshot loses nothing.
          activeConnections: state.activeConnections.map(active =>
            active.id === connection.id ? { ...active, connection: record } : active
          ),
        }));
        // When the save invalidates the live pool (e.g. enabling SSH on a
        // connected direct connection), the main process tore the pool down.
        // Drop the now-stale `connected` status so `connectToDatabase` doesn't
        // short-circuit and keep routing queries over the dead/old pool —
        // the next connect re-establishes through the new transport/target.
        // The same `options` main read: a password edit is a fact about this
        // save, not about the record that was just stored above.
        if (connectionRequiresPoolReset(prior, connection, options)) {
          const active = get().activeConnections.find(c => c.id === connection.id);
          if (active && active.status === 'connected') {
            get().markConnectionDisconnected(connection.id);
          }
        }
        // A database limit saved on this edit must evict a stale off-limit
        // Object Explorer selection: `setSelectedNodeId` stores the node's
        // database uncoerced, and nothing re-derives it until the user clicks
        // again — so surfaces seeded from the selection (Data Import, AI
        // export) would keep targeting a database the limit now excludes.
        const limitTarget = getLimitedDatabase(connection);
        if (limitTarget) {
          const ui = useDatabaseUIStore.getState();
          const selectionOwned =
            ui.selectedDatabaseConnectionId === connection.id ||
            (ui.selectedDatabase !== undefined && ui.selectedConnectionId === connection.id);
          if (selectionOwned && !isSameDatabaseName(ui.selectedDatabase, limitTarget)) {
            ui.clearSelectionForConnection(connection.id);
          }
        }
        // The pickers read this connection's database listing from the shared
        // cache and paint it BEFORE their revalidation lands, so an edit that
        // changes what `getDatabases` returns — the limit flag, or the declared
        // database — must leave nothing stale to paint. Removed, not
        // invalidated: an invalidated entry is still rendered.
        if (connectionEditChangesDatabaseListing(prior, connection)) {
          getSharedQueryClient()?.removeQueries({
            queryKey: databaseObjectsQueryKey.databases(connection.id),
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating connection:', error);
      return false;
    }
  },

  deleteConnection: async (connectionId: string) => {
    // A deleted id must never end connected (R2, round 5). Main refuses the
    // connects around a delete; this is the renderer's half: the id is marked
    // deleting for the whole IPC, so nothing connects or joins meanwhile, and
    // the dial in flight is WITHDRAWN so a refusal is not retried.
    deletingConnectionIds.add(connectionId);
    try {
      // Get engine before deleting for analytics
      const connectionToDelete = get().connections.find(c => c.id === connectionId);
      get().abandonConnect(connectionId);
      const outcome = await deleteConnectionService(connectionId);

      if (outcome.deleted) {
        set(state => ({
          connections: state.connections.filter(c => c.id !== connectionId),
          // Deleting a still-connected connection must also drop its
          // ActiveConnection — otherwise a dangling entry lingers (deletion
          // is a terminal path separate from disconnect). Idempotent: a
          // no-op when the connection was never active. Mirrors the exact
          // `disconnectFromDatabase` filter idiom.
          activeConnections: state.activeConnections.filter(
            (activeConn) => activeConn.id !== connectionId
          ),
        }));
        forgetSessionSshSecrets(connectionId);
        // Prune Package Explorer pins and last-viewed state for this connection.
        // Dynamic import avoids a circular dependency between the core connection
        // store and the package-explorer feature slice.
        try {
          const { usePackagePinsStore } = await import('../../workspace/package-explorer/store/packagePinsStore');
          usePackagePinsStore.getState().pruneConnection(connectionId);
        } catch {
          // Store module may not be loaded — ignore
        }
        // Clear any stale selections in databaseUIStore that reference this connection
        useDatabaseUIStore.getState().clearSelectionForConnection(connectionId);
        // Release IntelliSense schema-bus state — deletion is a separate
        // terminal path from disconnect (a still-connected connection can be
        // deleted), so it must drop bus snapshots + subscribers too.
        invalidateConnection(connectionId);
        clearSchemaRefreshesForConnection(connectionId);
        forgetMysqlSqlMode(connectionId);
        trackConnectionEvent('connection:delete', connectionToDelete?.engine);
        return true;
      }
      // Main did NOT delete it, and the two reasons need different words (A10).
      //
      // 'still-connected' — a REFUSAL (round 5): a handle that would not close
      // is still registered under this id, and removing the record would orphan
      // it. The record and the active entry are intact and the user can act.
      //
      // 'write-failed' — the pool and the tunnel ARE gone; only the record
      // write failed. Telling the user to disconnect first would be wrong on
      // both counts: there is nothing left to disconnect, and the fix is to
      // retry the delete (or fix whatever made connections.json unwritable).
      set((state) => ({
        connectionErrorById: {
          ...state.connectionErrorById,
          [connectionId]:
            outcome.reason === 'still-connected'
              ? 'Could not delete the connection: it is still connected. Disconnect it, then delete it again.'
              : 'The connection was disconnected, but its saved settings could not be removed. ' +
                'Check that the connections file is writable, then delete it again.',
        },
      }));
      return false;
    } catch (error) {
      console.error('Error deleting connection:', error);
      return false;
    } finally {
      deletingConnectionIds.delete(connectionId);
    }
  },

  connectToDatabase: async (
    connection: Connection,
    opts?: { force?: boolean; initiator?: ConnectInitiator }
  ) => {
    // Where `connection:connect`'s `readyBucket` is measured from: the moment
    // the action was entered, so the bucket covers everything the user waits
    // through (an on-connect password prompt, the keychain read, the IPC and
    // the dial), not just the part main can see. A caller that JOINS a dial
    // already in flight returns that dial's promise below and never reaches
    // the publish, so exactly one event is emitted per dial.
    const startedAt = Date.now();
    // Being deleted: nothing connects, nothing joins (round 5). Main refuses
    // the connects around a delete too; this is the half that stops the store
    // from asking, and from re-stamping a withdrawn dial's epoch.
    if (deletingConnectionIds.has(connection.id)) return false;

    const existingActiveConnection = get().activeConnections.find(
      (activeConn) => activeConn.id === connection.id
    );
    // Skip if already connected (not just exists, but actually connected) —
    // unless a force rebuild was requested (user-initiated Reconnect).
    if (!opts?.force && existingActiveConnection && existingActiveConnection.status === 'connected') {
      return true;
    }

    // Force must NOT join an in-flight request — a user-initiated Reconnect
    // asks for a fresh dial, not for whatever a possibly wedged one returns.
    // The new request replaces the map entry below, so later non-force callers
    // join the rebuild instead. (It can still be *delayed* by the incumbent:
    // main dials one connect per connection at a time, bounded by the driver's
    // own connect timeout. That ordering is what lets a withdrawn dial own —
    // and close — the pool it opened, instead of racing the rebuild's pool and
    // orphaning it.)
    const existingInFlight = inFlightConnectRequests.get(connection.id);
    if (existingInFlight && !opts?.force) {
      // Joining re-legitimizes the attempt. A user who cancels and then
      // immediately retries lands on the original dial, on its original
      // schedule, with nothing half-torn down — release only ever happens at
      // settle-time, and only if the withdrawal is still the last word.
      existingInFlight.epoch = currentIntentEpoch(connection.id);
      // Main has to hear about the revive too, or the dial this caller just
      // joined would still tear itself down at settle on the strength of a
      // withdrawal that no longer stands.
      void setConnectIntent(connection.id, existingInFlight.token, true);
      return existingInFlight.promise;
    }

    // A force reconnect supersedes whatever was dialing. Withdrawing it first
    // matters because main runs one connect per connection at a time: the
    // incumbent lands before the rebuild starts, and without this it would
    // land as a *wanted* connection and be adopted — the user would get the
    // stale pool they pressed Reconnect to get rid of. Withdrawn, it tears
    // itself down instead and the rebuild is what the user ends up on.
    if (opts?.force && existingInFlight) get().abandonConnect(connection.id);

    const attempt: ConnectAttempt = {
      // Assigned immediately below; the body only reads it after an await.
      promise: undefined as unknown as Promise<boolean>,
      epoch: currentIntentEpoch(connection.id),
      token: mintAttemptToken(connection.id),
    };

    const connectRequest = (async () => {
      set((state) => ({
        connectionOperationById: {
          ...state.connectionOperationById,
          [connection.id]: 'connecting',
        },
        connectionErrorById: {
          ...state.connectionErrorById,
          [connection.id]: undefined,
        },
        connectionErrorCodeById: {
          ...state.connectionErrorCodeById,
          [connection.id]: undefined,
        },
      }));

      try {
        // General on-connect password prompt (B2): if this connection needs a
        // password and none is stored or supplied, ask for it before the driver
        // fails. `connectForDriver` may carry the just-typed transient password;
        // `baseConnection` is what we persist (never plaintext) post-connect.
        let connectForDriver = connection;
        let baseConnection = connection;
        if (connectionNeedsPasswordPrompt(connection)) {
          const promptResult = await requestConnectPassword(connection);
          if (!promptResult) {
            // User cancelled the prompt — abort quietly (no error banner).
            return false;
          }
          connectForDriver = { ...connection, password: promptResult.password };
          if (promptResult.save) {
            // Persist the password (encrypted in the keychain) so future
            // connects don't prompt; keep plaintext out of the store record.
            await saveConnection(connectForDriver);
            baseConnection = { ...connection, hasStoredPassword: true };
          }
        }

        // Lazy password decryption: fetch password from keychain if stored but not provided
        let connectionWithPassword = connectForDriver;
        if (connectForDriver.hasStoredPassword && !connectForDriver.password) {
          const decryptedPassword = await getDecryptedPassword(connectForDriver.id);
          connectionWithPassword = { ...connectForDriver, password: decryptedPassword };
        }
        // The SSH secrets typed this session (not on the record — F5) go on the
        // DIAL config only: the tunnel manager reads them when no keychain blob
        // exists for the connection.
        connectionWithPassword = withTransientSshSecrets(
          connectionWithPassword,
          sessionSshSecretsFor(connectionWithPassword),
        );

        const activeConnection = await openConnection(connectionWithPassword, {
          ...opts,
          attemptToken: attempt.token,
        });

        if (activeConnection.status !== 'connected') {
          // Refused because the connection was DELETED (round 5). Not a dial
          // failure to report, and never the "ask again with force" retry
          // below — that retry is exactly how a deleted connection came back
          // to life with a pool, a tunnel and a re-created record.
          if (isConnectRefusedByDelete(activeConnection.error, activeConnection.errorCode)) {
            return false;
          }

          // Main closed a pool that HAD opened, because a withdrawal was
          // standing when the dial settled — and, per the check below, that
          // withdrawal is no longer the last word here. Withdrawals are keyed
          // on connection id, not on the attempt that asked, so one subsystem
          // (a validation pass aborting as its dialog closes, a teardown loop,
          // a lazy connect being forgotten) cancels whatever dial is in flight
          // for that id — including one another subsystem is waiting on. Ask
          // again instead of reporting a failure the user never caused: `force`
          // because the incumbent is gone, and it also bounds this to a single
          // retry (a forced attempt that comes back cancelled is reported).
          if (
            !opts?.force &&
            isConnectCancelledError(activeConnection.error) &&
            !isAttemptWithdrawn(connection.id, attempt)
          ) {
            return await get().connectToDatabase(connection, { ...opts, force: true });
          }

          if (isAttemptWithdrawn(connection.id, attempt)) {
            // Nobody is waiting on this any more, and a reported failure does
            // not prove nothing opened: our own client-side timeouts reject
            // locally while the server-side connect can still complete in the
            // background. Mop up rather than leave an invisible pool, and stay
            // silent — the user cancelled, so an error banner would be noise.
            await releaseWithdrawnConnect(connection.id, attempt);
            return false;
          }

          set((state) => ({
            connectionErrorById: {
              ...state.connectionErrorById,
              [connection.id]: redactSecretText(activeConnection.error || 'Failed to connect to database'),
            },
            connectionErrorCodeById: {
              ...state.connectionErrorCodeById,
              [connection.id]: activeConnection.errorCode,
            },
          }));

          // Track connection error with sanitized category + driver code
          trackConnectionError(
            connection.id,
            connection.engine,
            classifyDbError(connection.engine, activeConnection.error, activeConnection.errorCode)
          );
          return false;
        }

        if (isAttemptWithdrawn(connection.id, attempt)) {
          // The connect landed after the last caller walked away. Close it and
          // write nothing: no `lastConnectedAt` re-save, no `activeConnections`
          // entry, no `connection:connect` event — so the auto-save union in
          // sessionStore cannot pick up a connection the user said no to.
          await releaseWithdrawnConnect(connection.id, attempt);
          return false;
        }

        // Update lastConnectedAt timestamp. Stripped of the transient SSH
        // plaintext (a caller may still hand this action a connection straight
        // from the form): the record never carries it.
        const connectedAtIso = new Date().toISOString();
        const updatedConnection: Connection = {
          ...stripTransientSshSecrets(baseConnection),
          lastConnectedAt: connectedAtIso,
        };

        // Read BEFORE the publish below replaces it: did this caller dial with
        // a record the store does not hold? The one-click fix chips
        // (`SavedConnectionsList.handleApplyFix`) flip a dial field — trust the
        // certificate, turn encryption off, correct an Oracle service name —
        // and connect with it, and their contract is that a retry which WORKS
        // keeps the fix while a failed one leaves the saved settings alone.
        // That is the one case where a connect still writes a whole record;
        // every other connect dials the record as stored and only needs the
        // timestamp.
        //
        // Pre-existing and NOT introduced here: main compares that save against
        // the record on DISK, so it reads as a dial-field edit and tears down
        // the pool the retry just opened (`connectionRequiresPoolReset` →
        // `closeTunnelIfEnabledFlippedOff`). The unconditional post-connect
        // save did exactly the same; making it right means persisting the fix
        // BEFORE the retry, which is a change to the chip's contract rather
        // than to this path.
        const storedRecord = get().connections.find((c) => c.id === connection.id);
        const dialledWithUnsavedEdit =
          storedRecord !== undefined &&
          changedDialFields(storedRecord, updatedConnection).length > 0;

        // …and BECAUSE that teardown is real, this one save is awaited BEFORE
        // the publish — the ordering the unconditional post-connect save had.
        // Publishing first would announce the connection to everything that
        // watches `activeConnections` (the object explorer starts its database
        // listing and object preload immediately) and only then close the pool
        // underneath them, turning a chip that just reported success into a
        // screenful of error tiles. Publishing after keeps the whole teardown
        // inside the window the UI still renders as "connecting".
        if (dialledWithUnsavedEdit) {
          await saveConnection(updatedConnection);

          // An await is an await: the settle checks above were made BEFORE
          // this one, and the user can cancel, delete the connection or press
          // Reconnect while the save is in flight (a save that queues behind a
          // pool teardown in main is not quick). Withdrawal is token-scoped
          // and main honours it at SETTLE, so skipping this re-check publishes
          // an active connection — and fires `connection:connect` for it —
          // while main has already closed the pool behind it. Three ways to
          // lose the attempt, one outcome:
          //   • withdrawn — a cancel, a disconnect issued mid-dial, or the
          //     `abandonConnect` a delete does on its way in;
          //   • deleting — the record itself is on its way out, and main
          //     refuses every connect around a delete;
          //   • superseded — a force Reconnect replaced the in-flight entry,
          //     so the pool THIS attempt opened belongs to nobody.
          // Each releases this attempt's pool by TOKEN (never by bare id,
          // which would take down the successor's) and writes nothing: no
          // `activeConnections` entry, no timestamp, no analytics event.
          if (
            isAttemptWithdrawn(connection.id, attempt) ||
            deletingConnectionIds.has(connection.id) ||
            inFlightConnectRequests.get(connection.id) !== attempt
          ) {
            await releaseWithdrawnConnect(connection.id, attempt);
            return false;
          }
        }

        // PUBLISH FIRST, PERSIST AFTER — for every connect but the fix-chip
        // one above. This used to `await saveConnection` unconditionally — a
        // whole-record upsert whose main-side handler encrypts and rewrites
        // the entire connections file synchronously — BEFORE the connection
        // was published as active, so every connect paid that write before the
        // UI could show it connected. Nothing on the ordinary path needs the
        // write to have landed: the timestamp only orders the connections
        // list, and the store's own copy is updated in the same synchronous
        // `set` as the publish, so the ordering is right whether or not the
        // disk agrees yet.
        set((state) => {
          const existingIndex = state.activeConnections.findIndex(
            (activeConn) => activeConn.id === connection.id
          );
          const activeConnections =
            existingIndex >= 0
              ? state.activeConnections.map((entry, index) =>
                  // Reconnect after a disconnect: replace the existing entry.
                  index === existingIndex ? activeConnection : entry
                )
              : [...state.activeConnections, activeConnection];
          return {
            activeConnections,
            connections: state.connections.map(c =>
              c.id === connection.id ? updatedConnection : c
            ),
          };
        });

        // …and only now the disk, for the ORDINARY path — never awaited, since
        // a failed timestamp write is not a failed connect. Field-targeted
        // (`touch-connection-last-connected`): it stamps one field of one
        // record through the same mutation gate, so it cannot write this
        // possibly-stale copy of the record over an edit that landed during
        // the dial, and it writes nothing at all when the record was deleted
        // meanwhile. A connection the store does not hold is not a saved
        // connection, and neither write invents a record for it.
        if (!dialledWithUnsavedEdit && storedRecord) {
          void touchConnectionLastConnected(connection.id, connectedAtIso);
        }

        trackConnectionEvent('connection:connect', connection.engine, {
          // Action entry → the connection is published as active: the latency
          // the user actually waits through, including the password prompt,
          // the keychain read and the IPC, not just the dial main measures for
          // `feature:connect`. Bucketed — never a raw duration.
          readyBucket: connectDurationBucket(Date.now() - startedAt),
          // Session restore / auto-reconnect counts too, but must be separable
          // from a connect the user asked for: they have different cold-cache
          // and concurrency profiles. Absent means "not a restore" everywhere
          // else in the store (`shouldConsiderConnectForBridge`).
          initiator: opts?.initiator ?? 'user',
        });
        addBreadcrumb('connection', `connect:${connection.engine ?? 'unknown'}`);

        // First-connect bridge (Workstream C): only for genuine user connects,
        // never session-restore/auto-reconnect. Lazily imported to keep the
        // connect path lean and avoid a store<->workspace import cycle; the
        // bridge's own guard decides whether anything actually happens.
        if (shouldConsiderConnectForBridge(opts?.initiator)) {
          void import('../../workspace/first-connect-bridge')
            .then((m) => m.maybeRunFirstConnectBridge(connection))
            .catch(() => {});
        }
        return true;
      } catch (error) {
        console.error('Error connecting to database:', error);
        if (isAttemptWithdrawn(connection.id, attempt)) {
          // Same reasoning as the failed-status branch above: a thrown connect
          // may still have opened something server-side, and the user who
          // cancelled should not be shown an error about it.
          await releaseWithdrawnConnect(connection.id, attempt);
          return false;
        }

        set((state) => ({
          connectionErrorById: {
            ...state.connectionErrorById,
            [connection.id]:
              error instanceof Error ? redactSecretText(error.message) : 'Unknown connection error',
          },
          // Thrown (non-IPC-result) failures carry no structured code.
          connectionErrorCodeById: {
            ...state.connectionErrorCodeById,
            [connection.id]: undefined,
          },
        }));

        // Track connection error with sanitized category + driver code
        trackConnectionError(
          connection.id,
          connection.engine,
          classifyDbError(connection.engine, error instanceof Error ? error.message : String(error))
        );
        return false;
      } finally {
        set((state) => ({
          connectionOperationById: {
            ...state.connectionOperationById,
            [connection.id]: undefined,
          },
        }));
      }
    })();

    attempt.promise = connectRequest;
    inFlightConnectRequests.set(connection.id, attempt);
    // Identity check: a force reconnect may have replaced this entry — a
    // superseded request settling late must not evict the new one.
    void connectRequest.finally(() => {
      if (inFlightConnectRequests.get(connection.id) === attempt) {
        inFlightConnectRequests.delete(connection.id);
      }
    });
    return connectRequest;
  },

  abandonConnect: (connectionId: string) => {
    // One counter does both jobs: any in-flight attempt stamped with the old
    // value is now unauthorized (released on arrival), and any continuation
    // that captured the old value declines to write its bookkeeping.
    connectIntentEpochs.set(connectionId, currentIntentEpoch(connectionId) + 1);

    // Tell main as well, scoped to the attempt that is actually dialing. The
    // renderer alone can only decline to *adopt* a connection; a pool that
    // lands after the withdrawal is closed by main, at settle. Fire-and-forget
    // on purpose — cancelling must never block on the dial it cancels.
    const inFlight = inFlightConnectRequests.get(connectionId);
    if (inFlight) void setConnectIntent(connectionId, inFlight.token, false);
  },

  getConnectIntentEpoch: (connectionId: string) => currentIntentEpoch(connectionId),
  
  // Serves BOTH user-initiated disconnects and teardown (app close, session
  // switch, validation cleanup), so it deliberately does not touch the session.
  // User-initiated callers must additionally call
  // `useSessionStore.getState().removeConnectionFromSession(id)` on success —
  // see Docs/Session_Management.md, "Which connections a save keeps".
  disconnectFromDatabase: async (connectionId: string) => {
    // Last expression of intent wins. Disconnecting a connection that is still
    // dialing is otherwise a silent no-op in main (nothing owns it yet), and
    // the dial would land and be adopted afterwards — so record the withdrawal
    // here too, not just on the explicit cancel paths. A later retry still
    // revives the attempt via the join path.
    get().abandonConnect(connectionId);

    const existingInFlight = inFlightDisconnectRequests.get(connectionId);
    if (existingInFlight) {
      return existingInFlight;
    }

    // Get engine before disconnecting for analytics
    const connectionToDisconnect = get().connections.find(c => c.id === connectionId);
    // Snapshot BEFORE closing: `connection:disconnect` must mean "an open
    // connection ended". This action also serves cleanup callers (session
    // validation closing a probe that never connected, teardown of an entry
    // already marked disconnected), and the no-op close still reports success —
    // without the gate every failed restore probe emits a phantom disconnect,
    // 1:1 with its `connection:error`.
    const wasConnected = get().activeConnections.some(
      (activeConn) => activeConn.id === connectionId && activeConn.status === 'connected'
    );

    const disconnectRequest = (async () => {
      set((state) => ({
        connectionOperationById: {
          ...state.connectionOperationById,
          [connectionId]: 'disconnecting',
        },
        connectionErrorById: {
          ...state.connectionErrorById,
          [connectionId]: undefined,
        },
        connectionErrorCodeById: {
          ...state.connectionErrorCodeById,
          [connectionId]: undefined,
        },
      }));

      try {
        const outcome = await closeConnection(connectionId);

        // The user asked for this connection again, so main answers "still
        // WANTED, not disconnected". Two shapes reach here (see
        // `CloseConnectionOutcome`): the connect arrived while the disconnect
        // was queued, so main stood down and the pool is STILL UP; or it
        // arrived while the teardown ran, so the pool is gone and the queued
        // connect is about to dial — that flow supplies the CONNECTING
        // rendering. Leaving the entry in `activeConnections` is the whole
        // point either way — removing it here is what made the sidebar drop a
        // connection and bring it straight back.
        if (outcome === 'superseded') {
          return false;
        }

        if (outcome) {
          set((state) => ({
            activeConnections: state.activeConnections.filter(
              (activeConn) => activeConn.id !== connectionId
            ),
          }));
          // Clear any stale selections in databaseUIStore that reference this connection
          useDatabaseUIStore.getState().clearSelectionForConnection(connectionId);
          // Invalidate metadata caches so stale hints aren't served on reconnect
          invalidateRoutines(connectionId);
          invalidateColumns(connectionId);
          // Drop the IntelliSense schema-bus snapshots + subscribers for this
          // connection (notifies subscribers with a `disconnected` event).
          // Reconnect = disconnect-then-connect, so this also fixes
          // stale-DB-on-reconnect.
          invalidateConnection(connectionId);
          clearSchemaRefreshesForConnection(connectionId);
          // The DDL invalidator's masker remembers this connection's MySQL
          // `sql_mode` across the window where the schema snapshot is dropped
          // (`workspace/common/utils/mysqlSqlMode.ts`). The app reuses a
          // connection id across a reconnect and the next server may run a
          // different mode, so the remembered value dies with the connection —
          // the same rule the navigational layer's `forgetCasePolicy` follows.
          forgetMysqlSqlMode(connectionId);
          if (wasConnected) {
            trackConnectionEvent('connection:disconnect', connectionToDisconnect?.engine);
          }
          addBreadcrumb('connection', `disconnect:${connectionToDisconnect?.engine ?? 'unknown'}`);
          return true;
        }

        set((state) => ({
          connectionErrorById: {
            ...state.connectionErrorById,
            [connectionId]: 'Failed to disconnect from database',
          },
        }));
        return false;
      } catch (error) {
        console.error('Error disconnecting from database:', error);
        set((state) => ({
          connectionErrorById: {
            ...state.connectionErrorById,
            [connectionId]:
              error instanceof Error ? redactSecretText(error.message) : 'Unknown disconnection error',
          },
        }));
        return false;
      } finally {
        inFlightDisconnectRequests.delete(connectionId);
        set((state) => ({
          connectionOperationById: {
            ...state.connectionOperationById,
            [connectionId]: undefined,
          },
        }));
      }
    })();

    inFlightDisconnectRequests.set(connectionId, disconnectRequest);
    return disconnectRequest;
  },

  markConnectionDisconnected: (connectionId: string) => {
    set((state) => ({
      activeConnections: state.activeConnections.map((conn) =>
        conn.id === connectionId
          ? { ...conn, status: 'disconnected' as const }
          : conn
      ),
    }));
    // Clear any stale selections in databaseUIStore that reference this connection
    useDatabaseUIStore.getState().clearSelectionForConnection(connectionId);
    // A lost connection ends any manual IntelliSense refresh in flight on it
    // — same as the explicit disconnect path — so the explorer spinner and
    // the pair's dedupe do not outlive the dead refetch.
    clearSchemaRefreshesForConnection(connectionId);
    // …and, for the same reason, the remembered MySQL `sql_mode`: a dropped
    // connection may come back pointed at a differently-configured server.
    forgetMysqlSqlMode(connectionId);
  },

  setTunnelStateForConnection: (connectionId, tunnelState) => {
    // M12 — bail when nothing actually changes so subscribers to
    // activeConnections don't re-render on every ssh:tunnel-state IPC event.
    // With N active connections, the prior map() returned a new array even on
    // identity values, re-rendering every card on each broadcast.
    set((state) => {
      const idx = state.activeConnections.findIndex((c) => c.id === connectionId);
      if (idx < 0) return state;
      if (state.activeConnections[idx].tunnelState === tunnelState) return state;
      const next = [...state.activeConnections];
      next[idx] = { ...next[idx], tunnelState };
      return { activeConnections: next };
    });
  },
}));

// Fix #5 — subscribe once at module load so the store stays in sync with
// main-process tunnel-state events. The `ssh:tunnel-state` channel is
// broadcast on every transition (connecting / up / down / error).
//
// F10 — keep the renderer in sync with main-process SSH tunnel-state events,
// subscribing exactly once. Vite HMR re-evaluates this module on edit; without
// teardown every hot-reload stacks another ipcRenderer listener, eventually
// tripping "MaxListenersExceededWarning" and firing the store update N times.
// We stash the unsubscribe on `window` and drop the previous listener before
// re-subscribing — same effect as a Vite HMR `dispose` hook, but without
// `import.meta`, which the Playwright/babel CommonJS transform can't load when
// an E2E spec transitively imports this store (it forces the module into ES
// scope and breaks test collection).
if (typeof window !== 'undefined' && window.electronAPI?.ssh?.onTunnelStateChange) {
  const w = window as unknown as { __jamTunnelStateUnsub?: () => void };
  try { w.__jamTunnelStateUnsub?.(); } catch { /* ignore */ }
  w.__jamTunnelStateUnsub = window.electronAPI.ssh.onTunnelStateChange((evt) => {
    useConnectionStore.getState().setTunnelStateForConnection(evt.connectionId, evt.state);
  });
}

